import {
    addDevCounter,
    renderAsCurrency,
    renderCoOwning,
    renderFloorAccessor,
    renderParkingLot,
    renderPrimaryArea,
    renderPurchase,
    renderTotalPrice,
    renderTotalPriceJmoy,
    roomRenderer,
} from './renderers';
import {LOCALE} from "features/ui/enums";
import {SearchResult} from "features/search/types";
import {User} from "features/authentication/models/User";
import {DisposalForm, ResidenceType} from "features/search/filters/enums";
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {getCurrentLocale, getLocalizedKvm} from "features/services/utils/env-helper";

import {AccessorKeyColumnDef, createColumnHelper,} from '@tanstack/react-table';
import {renderInhabitDate} from 'features/services/utils/date-helpers';

export const getPriceListTableColumns = (
    data: SearchResult[], user: User | null | undefined,
    isCoOwning: boolean, isMultiProject: boolean,
    disposalForm: DisposalForm, constructionForm: ResidenceType
) => {
    const locale = getCurrentLocale();
    const isFinnish = locale === LOCALE.FI;
    const isNorwegian = locale === LOCALE.NO;
    const _hasBraInternal = isNorwegian && true;

    const columnHelper = createColumnHelper<SearchResult>();

    const columnId = columnHelper.accessor('id', {
        id: 'pricelist-id',
        cell: info => renderPurchase(user)(info.getValue()),
        header: () => addDevCounter(2),
    })

    const columnRoom = columnHelper.accessor('rooms', {
        id: 'pricelist-rooms',
        cell: info => roomRenderer(info.getValue()?.toString(), info.row.original),
        //cell: info => getLocalizedRok(info.getValue()),
        header: () => addDevCounter(3) + getFormattedMessage('jm.components.Pricelist.rooms'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortRooms"
    })

    const columnPrice = columnHelper.accessor('price', {
        id: 'pricelist-price',
        cell: info => renderAsCurrency(info.getValue()?.toString(), info.row.original),
        //cell: info => (info.row.original.objectStatus === ObjectStatus.FOR_SALE || info.row.original.objectStatus === ObjectStatus.RESERVED) ? getLocalizedPrice(info.getValue()) : '',
        header: () => addDevCounter(4) + getFormattedMessage('jm.components.Pricelist.price'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"

    })
    const columnTotalPrice = columnHelper.accessor('price', {
        id: 'pricelist-totalPrice',
        cell: info => renderTotalPrice(info.getValue(), info.row.original),
        //cell: info => (info.row.original.objectStatus === ObjectStatus.FOR_SALE || info.row.original.objectStatus === ObjectStatus.RESERVED) ? getLocalizedPrice(info.getValue()) : '',
        header: () => addDevCounter(5) + getFormattedMessage('jm.components.Pricelist.totalPrice'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortTotalPrice"

    })
    const columnTotalPriceJmoy = columnHelper.accessor('jmoyTotalPrice', {
        id: 'pricelist-jmoyTotalPrice',
        cell: info => renderTotalPriceJmoy(info.getValue(), info.row.original),
        //cell: info => (info.row.original.objectStatus === ObjectStatus.FOR_SALE || info.row.original.objectStatus === ObjectStatus.RESERVED) ? getLocalizedPrice(info.getValue()) : '',
        header: () => addDevCounter(6) + getFormattedMessage('jm.components.Pricelist.totalPrice'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"
    })

    const columnFee = columnHelper.accessor('fee', {
        id: 'pricelist-fee',
        cell: info => renderAsCurrency(info.getValue(), info.row.original),
        //cell: info => (info.row.original.objectStatus === ObjectStatus.FOR_SALE || info.row.original.objectStatus === ObjectStatus.RESERVED) ? getLocalizedPrice(info.getValue()) : '',
        header: () => addDevCounter(7) + getFormattedMessage('jm.components.Pricelist.fee'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"
    })
    const columnCommunityFee = columnHelper.accessor('fee', {
        id: 'pricelist-communityFee',
        cell: info => renderAsCurrency(info.getValue(), info.row.original),
        //cell: info => (info.row.original.objectStatus === ObjectStatus.FOR_SALE || info.row.original.objectStatus === ObjectStatus.RESERVED) ? getLocalizedPrice(info.getValue()) : '',
        header: () => addDevCounter(8) + getFormattedMessage('jm.components.Pricelist.communityFee'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"
    })

    const columnPrimaryArea = columnHelper.accessor('primaryArea', {
        id: 'pricelist-primaryArea',
        cell: info => renderPrimaryArea(info.getValue(), info.row.original),
        //cell: info => getLocalizedKvm(info.getValue()),
        header: () => addDevCounter(9) + getFormattedMessage('jm.components.Pricelist.area'),
        accessorFn: info => (getCurrentLocale() === LOCALE.NO && info.jmnoBraInternal) ? info.jmnoBraInternal : info.primaryArea,
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortPrimaryArea"
    })
    const columnSecondaryArea = columnHelper.accessor('secondaryArea', {
        id: 'pricelist-secondaryArea',
        cell: info => info.getValue(),
        header: () => addDevCounter(10) + getFormattedMessage('jm.components.Pricelist.secondaryArea'),
        //sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"
    })
    const columnPlotArea = columnHelper.accessor('plotArea', {
        id: 'pricelist-plotArea',
        cell: info => getLocalizedKvm(info.getValue()),
        header: () => addDevCounter(11) + getFormattedMessage('jm.components.Pricelist.plot'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"
    })

    const columnFloor = columnHelper.accessor('floor', {
        id: 'pricelist-floor',
        cell: info => renderFloorAccessor(info.getValue(), info.row.original),
        //cell: info => `${info.getValue()} / ${info.row.original.maxFloor}`,
        header: () => addDevCounter(12) + getFormattedMessage('jm.components.Pricelist.floor'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortFloor"
    })

    const columnInhabitDate = columnHelper.accessor('inhabitDate', {
        id: 'pricelist-inhabitDate',
        cell: info => renderInhabitDate(info.getValue()?.toString(), info.row.original.inhabitDateEnd, info.row.original.inhabitDateDetermined),
        header: () => addDevCounter(13) + getFormattedMessage('jm.components.Pricelist.inhabitDate'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortDate"
    })

    const columnApartmentNumber = columnHelper.accessor('apartmentNumber', {
        id: 'pricelist-apartmentNumber',
        cell: info => info.getValue(),
        header: () => addDevCounter(14) + getFormattedMessage('jm.components.Pricelist.apartmentNumber'),
        sortUndefined: 'last',
        sortingFn: 'alphanumeric'//"sortWithObjectStatus"
    })
    const columnAddress = columnHelper.accessor('address', {
        id: 'pricelist-address',
        cell: info => info.getValue(),
        header: () => addDevCounter(15) + getFormattedMessage('jm.components.Pricelist.address'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"
    })

    const columnParkinglot = columnHelper.accessor('parkingSpaceStatus', {
        id: 'pricelist-parkingspaceStatus',
        cell: info => renderParkingLot(info.getValue(), info.row.original),
        header: () => addDevCounter(16) + getFormattedMessage('jm.components.Pricelist.parkinglot'),
        // @ts-expect-error
        sortingFn: "sortParking"
    })

    const columnCoOwning = columnHelper.accessor('possibleCoOwning', {
        id: 'pricelist-possibleCoOwning',
        cell: info => renderCoOwning(info.getValue(), info.row.original),
        header: () => addDevCounter(17) + getFormattedMessage('jm.components.Pricelist.coOwning'),
        // @ts-expect-error
        sortingFn: "sorCoOwning"
    })

    const columnSharedCost = columnHelper.accessor('jmnoSharedCost', {
        id: 'pricelist-jmnoSharedCost',
        cell: info => renderAsCurrency(info.getValue(), info.row.original),
        header: () => addDevCounter(18) + getFormattedMessage('jm.components.Pricelist.communityFee'),
        sortUndefined: 'last',
        // @ts-expect-error
        sortingFn: "sortWithObjectStatus"
    })

    const finnishColumns = isCoOwning
        ? [columnRoom, columnPrimaryArea, columnPrice, columnTotalPriceJmoy, columnCoOwning, columnFloor, columnApartmentNumber]
        : [columnRoom, columnPrimaryArea, columnPrice, columnTotalPriceJmoy, columnFloor, columnApartmentNumber];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TanStack Table AccessorKeyColumnDef generic value type
    const buildColumnList = (columns:  AccessorKeyColumnDef<SearchResult, any>[]) => {
        if (isMultiProject && isNorwegian) {
            columns.splice(3, 1, columnInhabitDate);
        }
        if (hasSecondaryArea && !isFinnish) {
            columns.splice(5, 0, columnSecondaryArea);
        }
        if (isCoOwning && !isFinnish) { // Finland gets multiple co-owning columns if added like this
            columns.splice(4, 0, columnCoOwning);
        }
        return columns;
    };

    const hasSecondaryArea = data.some((object) => object.secondaryArea != null && object.secondaryArea != 0);

    const columnsApartment = buildColumnList(
        isNorwegian
            ? [columnId, columnRoom, columnTotalPrice, columnPrice, columnPrimaryArea, columnFloor, columnParkinglot, columnApartmentNumber]
            : isFinnish
                ? finnishColumns
                : [columnId, columnRoom, columnPrice, columnFee, columnPrimaryArea, columnFloor, columnInhabitDate, columnApartmentNumber, columnAddress]
    );

    const columnsApartmentOwnership = buildColumnList(
        isNorwegian
            ? [columnId, columnRoom, columnTotalPrice, columnPrice, columnSharedCost, columnPrimaryArea, columnFloor, columnParkinglot, columnApartmentNumber]
            : isFinnish
                ? finnishColumns
                : [columnId, columnRoom, columnPrice, columnCommunityFee, columnPrimaryArea, columnFloor, columnInhabitDate, columnParkinglot, columnApartmentNumber, columnAddress]
    );

    const columnsHouse = buildColumnList(
        isNorwegian
            ? [columnId, columnRoom, columnTotalPrice, columnPrice, columnPrimaryArea, columnParkinglot, columnApartmentNumber]
            : isFinnish
                ? finnishColumns
                : [columnId, columnRoom, columnPrice, columnFee, columnPrimaryArea, columnPlotArea, columnInhabitDate, columnApartmentNumber, columnAddress]
    );

    const columnsHouseOwnership = buildColumnList(
        isNorwegian
            ? [columnId, columnRoom, columnTotalPrice, columnPrice, columnPlotArea, columnPrimaryArea, columnParkinglot, columnApartmentNumber]
            : isFinnish
                ? finnishColumns
                : [columnId, columnRoom, columnPrice, columnCommunityFee, columnPrimaryArea, columnPlotArea, columnInhabitDate, columnApartmentNumber, columnAddress]
    );

    const columnsDefault = buildColumnList(
        isNorwegian
            ? [columnId, columnRoom, columnTotalPrice, columnPrice, columnSharedCost, columnPrimaryArea, columnFloor, columnParkinglot, columnApartmentNumber]
            : isFinnish
                ? finnishColumns
                : [columnId, columnRoom, columnPrice, columnFee, columnPrimaryArea, columnFloor, columnInhabitDate, columnApartmentNumber, columnAddress]
    );

    const columnsPlot = buildColumnList(
        isNorwegian
            ? [columnId, columnApartmentNumber, columnTotalPrice, columnPrice, columnPlotArea, columnAddress]
            : isFinnish
                ? finnishColumns
                : [columnId, columnApartmentNumber, columnPrice, columnPlotArea, columnAddress]
    );

    if (constructionForm == ResidenceType.APARTMENT) {
        return disposalForm == DisposalForm.OWNERSHIP_APARTMENT || disposalForm == DisposalForm.OWNERSHIP
            ? columnsApartmentOwnership
            : columnsApartment;
    }

    if (
        constructionForm == ResidenceType.HOUSE ||
        constructionForm == ResidenceType.TOWN_HOUSE ||
        constructionForm == ResidenceType.DETACHED_HOUSE ||
        constructionForm == ResidenceType.SEMI_DETACHED_HOUSE
    ) {
        return disposalForm == DisposalForm.OWNERSHIP
            ? columnsHouseOwnership
            : columnsHouse;
    }

    if (constructionForm == ResidenceType.PLOT) {
        return columnsPlot;
    }

    return columnsDefault;
};
